'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertTriangle, ExternalLink, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Third-party link guard for lobby metadata.
 *
 * Board- and adapter-level links (Twitter/X, Telegram, project websites,
 * adapter pools, Dexscreener, docs, …) are user- or protocol-authored
 * metadata that we surface in the UI but don't vet. Before opening any
 * of them we show a short modal that:
 *
 *   1. States we're leaving takeover.app.
 *   2. Shows the **full destination URL** so users can eyeball path and
 *      query before committing.
 *   3. Reminds them to Do Your Own Research before signing, funding, or
 *      trusting anything on the other side.
 *
 * A "don't ask again this session" opt-out is persisted in
 * `sessionStorage` so power users aren't pestered on every click, but
 * the default is opt-in: the first time you click a third-party link
 * in a tab, you'll always see the modal.
 *
 * ## Usage
 *
 * ```tsx
 * const { guardClick } = useExternalLinkGuard();
 *
 * <a
 *   href={href}
 *   target="_blank"
 *   rel="noreferrer"
 *   onClick={guardClick(href)}
 * >
 *   Visit
 * </a>
 * ```
 *
 * `guardClick(href)` returns a click handler that intercepts the click
 * (`preventDefault`) and shows the modal; when the user confirms, the
 * link is opened via `window.open(..., 'noopener,noreferrer')`. The
 * underlying `<a>` keeps its real `href` so middle-clicks and
 * right-click-copy-link still work without the warning — the guard is
 * a safety net on the common path, not a lock.
 */

interface ExternalLinkGuardValue {
  /**
   * Wrap an anchor `onClick` to route the destination through the
   * warning dialog. Returns a handler that preventDefaults and opens
   * the modal; once confirmed it fires `window.open(href)`.
   */
  guardClick: (
    href: string,
  ) => (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  /**
   * User-authored URLs from board metadata (e.g. “Site”) — always show the
   * interstitial, even when the host would otherwise be trusted. Session
   * “don’t ask again” still applies.
   */
  guardMetadataClick: (
    href: string,
  ) => (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  /** Imperative variant for non-anchor triggers (e.g. menu items). */
  confirmAndOpen: (href: string) => void;
}

const GuardContext = createContext<ExternalLinkGuardValue | null>(null);

/** sessionStorage key for the "don't ask again" checkbox. */
const SUPPRESS_KEY = 'takeover.externalLinkGuard.suppressed';

/**
 * Destinations we consider safe enough to skip the interstitial modal
 * even on the first click in a session. These are either:
 *   - first-party (our own apps / legacy host)
 *   - well-known major platforms where the brand is easy to recognise
 *     in the URL bar (X / Twitter / Warpcast)
 *   - block explorers + DEX dashboards we build transaction URLs for
 *     ourselves (the host is chosen by our code, not by user metadata)
 *
 * Anything outside this list is user- or protocol-authored and gets
 * the DYOR modal on first click in the session. A suffix match is
 * allowed so staging / regional subdomains (`go.x.com`, `app.safe.global`
 * when proxied, etc.) don't surprise users with the warning.
 */
const TRUSTED_HOSTS = [
  'takeover.fun',
  'classic.takeover.fun',
  'x.com',
  'twitter.com',
  'warpcast.com',
  'basescan.org',
  'sepolia.basescan.org',
  'etherscan.io',
  'base.org',
  'flaunch.gg',
] as const;

function isTrustedHost(href: string): boolean {
  try {
    const host = new URL(href).hostname.replace(/^www\./, '');
    return TRUSTED_HOSTS.some((d) => host === d || host.endsWith(`.${d}`));
  } catch {
    return false;
  }
}

export function useExternalLinkGuard(): ExternalLinkGuardValue {
  const ctx = useContext(GuardContext);
  if (ctx) return ctx;
  // Fallback if a caller mounts outside the provider (e.g. a page
  // route that forgot to include it) — just open the link directly
  // rather than crash the UI.
  return {
    guardClick: (href) => (event) => {
      event.preventDefault();
      if (typeof window !== 'undefined')
        window.open(href, '_blank', 'noopener,noreferrer');
    },
    guardMetadataClick: (href) => (event) => {
      event.preventDefault();
      if (typeof window !== 'undefined')
        window.open(href, '_blank', 'noopener,noreferrer');
    },
    confirmAndOpen: (href) => {
      if (typeof window !== 'undefined')
        window.open(href, '_blank', 'noopener,noreferrer');
    },
  };
}

export function ExternalLinkGuardProvider({ children }: { children: ReactNode }) {
  const [pendingHref, setPendingHref] = useState<string | null>(null);
  const [dontAskAgain, setDontAskAgain] = useState(false);
  // We check sessionStorage lazily per-call so a user who opts in on
  // one tab but not another doesn't bleed state between them.
  const suppressRef = useRef<boolean | null>(null);

  const isSuppressed = useCallback(() => {
    if (suppressRef.current !== null) return suppressRef.current;
    if (typeof window === 'undefined') return false;
    try {
      const v = window.sessionStorage.getItem(SUPPRESS_KEY);
      suppressRef.current = v === '1';
      return suppressRef.current;
    } catch {
      suppressRef.current = false;
      return false;
    }
  }, []);

  const openExternal = useCallback((href: string) => {
    if (typeof window === 'undefined') return;
    window.open(href, '_blank', 'noopener,noreferrer');
  }, []);

  const confirmAndOpen = useCallback(
    (href: string) => {
      if (!href) return;
      if (isSuppressed() || isTrustedHost(href)) {
        openExternal(href);
        return;
      }
      setPendingHref(href);
      setDontAskAgain(false);
    },
    [isSuppressed, openExternal],
  );

  const guardClick = useCallback(
    (href: string) =>
      (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        // Let modifier-clicks (cmd/ctrl/shift/middle-click) pass
        // through to the native target — those open in background
        // tabs / new windows and users doing them know what they're
        // doing. Guarding them would break muscle memory.
        if (
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          event.button === 1
        ) {
          return;
        }
        event.preventDefault();
        confirmAndOpen(href);
      },
    [confirmAndOpen],
  );

  /** Metadata “Site” / user URLs — never skip the modal via TRUSTED_HOSTS. */
  const guardMetadataClick = useCallback(
    (href: string) =>
      (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        if (
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          event.button === 1
        ) {
          return;
        }
        event.preventDefault();
        if (!href) return;
        if (isSuppressed()) {
          openExternal(href);
          return;
        }
        setPendingHref(href);
        setDontAskAgain(false);
      },
    [isSuppressed, openExternal],
  );

  const handleContinue = useCallback(() => {
    if (!pendingHref) return;
    if (dontAskAgain) {
      try {
        window.sessionStorage.setItem(SUPPRESS_KEY, '1');
        suppressRef.current = true;
      } catch {
        // If storage is blocked we still proceed; the user will just
        // see the modal again next time.
      }
    }
    const href = pendingHref;
    setPendingHref(null);
    openExternal(href);
  }, [dontAskAgain, openExternal, pendingHref]);

  const handleCancel = useCallback(() => {
    setPendingHref(null);
  }, []);

  const value = useMemo<ExternalLinkGuardValue>(
    () => ({ guardClick, guardMetadataClick, confirmAndOpen }),
    [confirmAndOpen, guardClick, guardMetadataClick],
  );

  return (
    <GuardContext.Provider value={value}>
      {children}
      <ExternalLinkDialog
        href={pendingHref}
        dontAskAgain={dontAskAgain}
        onToggleDontAskAgain={setDontAskAgain}
        onContinue={handleContinue}
        onCancel={handleCancel}
      />
    </GuardContext.Provider>
  );
}

function ExternalLinkDialog({
  href,
  dontAskAgain,
  onToggleDontAskAgain,
  onContinue,
  onCancel,
}: {
  href: string | null;
  dontAskAgain: boolean;
  onToggleDontAskAgain: (value: boolean) => void;
  onContinue: () => void;
  onCancel: () => void;
}) {
  const open = href !== null;

  // Keep the last-known host rendered through the exit animation so
  // the text doesn't flicker to empty before the close transition
  // completes.
  const [shownHref, setShownHref] = useState<string | null>(href);
  useEffect(() => {
    if (href) setShownHref(href);
  }, [href]);

  /** Full URL for display (users verify path + query, not just the host). */
  const displayUrl = useMemo(() => {
    if (!shownHref) return '';
    try {
      return new URL(shownHref).toString();
    } catch {
      return shownHref;
    }
  }, [shownHref]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent
        className={cn(
          'border-amber-400/40 bg-gradient-to-b from-[#1b1509] to-[#0a0805]',
          'text-amber-50 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]',
          'max-w-md',
        )}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-[4px] border border-amber-400/40 bg-amber-500/10">
              <AlertTriangle className="h-4 w-4 text-amber-200" />
            </span>
            <DialogTitle className="text-amber-100 tracking-[0.08em]">
              Leaving takeover.fun
            </DialogTitle>
          </div>
          <DialogDescription className="text-zinc-300 text-[13px] leading-relaxed">
            You're about to open a third-party page in a new tab. Double-check
            the destination before signing anything or sending funds.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 pt-1">
          <div className="rounded-[4px] border border-amber-500/25 bg-black/40 px-3 py-2">
            <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-zinc-500">
              Destination
            </div>
            <div className="mt-0.5 flex items-start gap-1.5 text-[11.5px] font-medium text-amber-100 break-all">
              <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-70 mt-0.5" />
              <span title={displayUrl}>{displayUrl}</span>
            </div>
          </div>

          <div className="flex items-start gap-2 rounded-[4px] border border-amber-500/15 bg-amber-500/[0.04] px-3 py-2 text-[12px] leading-snug text-zinc-300">
            <Shield className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-200/80" />
            <div>
              <span className="font-semibold text-amber-100">
                Do Your Own Research.
              </span>{' '}
              We show board and adapter links as-is — we don't vet, endorse, or
              control third-party sites. Verify URLs, contracts, and claims
              independently before acting.
            </div>
          </div>

          <label className="flex items-center gap-2 text-[11px] text-zinc-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontAskAgain}
              onChange={(event) => onToggleDontAskAgain(event.target.checked)}
              className="h-3 w-3 accent-amber-400"
            />
            Don't show this again for the rest of my session
          </label>
        </div>

        <DialogFooter className="mt-2">
          <button
            type="button"
            onClick={onCancel}
            className={cn(
              'h-8 rounded-[4px] border border-white/15 bg-transparent px-4',
              'text-[11px] font-mono tracking-[0.18em] uppercase text-zinc-300',
              'hover:text-amber-100 hover:border-amber-400/40 transition-colors',
            )}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onContinue}
            className={cn(
              'h-8 rounded-[4px] border border-amber-400/50 bg-amber-500/15 px-4',
              'text-[11px] font-mono tracking-[0.18em] uppercase text-amber-100',
              'hover:bg-amber-500/25 hover:border-amber-300/70 transition-colors',
              'inline-flex items-center gap-1.5',
            )}
          >
            Continue
            <ExternalLink className="h-3 w-3 opacity-80" />
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
